import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import './Support.css';
import { MEMBERSHIP_PRICES } from '../../config/paypal';
import { sendMembershipEmail } from '../../utils/emailUtils';

// Validation schema for the membership form
const membershipSchema = Yup.object().shape({
  applicationType: Yup.string().required('Application type is required'),
  membershipType: Yup.string().required('Membership type is required'),
  firstPerson: Yup.object().shape({
    fullName: Yup.string().required('Full name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone: Yup.string(),
  }),
  secondPerson: Yup.object().nullable(),
  address: Yup.object().shape({
    mailing: Yup.string().required('Mailing address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zipCode: Yup.string().required('Zip code is required'),
    zipExt: Yup.string(),
  }),
});

// Custom validation function for second person
const validateSecondPerson = (values) => {
  const errors = {};
  if (values.membershipType === 'family') {
    if (!values.secondPerson?.fullName) {
      errors.secondPerson = { fullName: 'Secondary member name is required' };
    }
    if (!values.secondPerson?.email) {
      errors.secondPerson = { ...errors.secondPerson, email: 'Secondary member email is required' };
    }
    if (values.secondPerson?.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.secondPerson.email)) {
      errors.secondPerson = { ...errors.secondPerson, email: 'Invalid email address' };
    }
  }
  return errors;
};

// Initial form values
const initialValues = {
  applicationType: 'new',
  membershipType: 'individual',
  firstPerson: {
    fullName: '',
    email: '',
    phone: '',
  },
  secondPerson: null,
  address: {
    mailing: '',
    city: '',
    state: '',
    zipCode: '',
    zipExt: '',
  },
};

function Support() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('membership');
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);

  const presetAmounts = [25, 50, 100, 250];
  const membershipOptions = [
    { id: 'individual', label: 'Individual (1 year)', price: MEMBERSHIP_PRICES.individual },
    { id: 'family', label: 'Family (1 year)', price: MEMBERSHIP_PRICES.family },
    { id: 'senior', label: 'Senior 65+ (1 year)', price: MEMBERSHIP_PRICES.senior },
    { id: 'lifetime', label: 'Lifetime Membership', price: MEMBERSHIP_PRICES.lifetime }
  ];

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'donate') {
      setActiveTab('donate');
    }
  }, [searchParams]);

  const handlePaymentSuccess = async (values, actions) => {
    setIsProcessing(true);
    try {
      const order = await actions.order.capture();
      
      const emailSent = await sendMembershipEmail({
        ...values,
        paymentDetails: order,
        amount: membershipOptions.find(opt => opt.id === values.membershipType)?.price || 0
      });
      
      if (emailSent) {
        alert('Payment successful! Your membership has been confirmed and an email has been sent.');
        setError(null);
      } else {
        // Payment succeeded; email is best-effort.
        alert('Payment successful! Your membership has been confirmed, but we were unable to send the confirmation email. If you do not receive an email, please contact us.');
        setError('Payment was successful, but there was an error sending the confirmation email. Please contact us if you did not receive an email receipt.');
      }
      actions.resetForm();
      
    } catch (error) {
      console.error('Error processing payment:', error);
      setError('There was an error processing your payment. Please try again or contact us for assistance.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAmountSelect = (amount) => {
    setSelectedPreset(amount);
    setDonationAmount(amount.toString());
  };

  const handleCustomAmount = (e) => {
    const value = e.target.value;
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
      setDonationAmount(value);
      setSelectedPreset(null);
    }
  };

  const renderDonationSection = () => (
    <div className="donation-section">
      <div className="donation-intro section">
        <h2>Donate to Us and Support:</h2>
        <div className="donation-benefits">
          <div className="benefit-card">
            <h3>Research & Archives</h3>
            <p>Support historical research and document preservation efforts</p>
          </div>
          <div className="benefit-card">
            <h3>Educational Programs</h3>
            <p>Help fund our educational initiatives and public programs</p>
          </div>
          <div className="benefit-card">
            <h3>Community Outreach</h3>
            <p>Enable us to engage with more community members and share our history</p>
          </div>
        </div>
      </div>

      <div className="donation-form">
        <div className="amount-selection">
          <h3>Select Donation Amount</h3>
          <div className="preset-amounts">
            {presetAmounts.map(amount => (
              <button
                key={amount}
                className={`amount-button ${selectedPreset === amount ? 'active' : ''}`}
                onClick={() => handleAmountSelect(amount)}
              >
                ${amount}
              </button>
            ))}
          </div>
          <div className="custom-amount">
            <label htmlFor="customAmount">Custom Amount</label>
            <div className="input-wrapper">
              <span className="currency-symbol">$</span>
              <input
                id="customAmount"
                type="number"
                value={donationAmount}
                onChange={handleCustomAmount}
                placeholder="Enter amount"
                min="1"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {donationAmount && parseFloat(donationAmount) > 0 && (
          <div className="paypal-container">
            <PayPalScriptProvider options={{
              "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
              currency: "USD",
              intent: "capture"
            }}>
              <div className="paypal-buttons-container">
                <PayPalButtons
                  style={{
                    layout: "vertical",
                    shape: "rect",
                    label: "donate",
                    height: 55
                  }}
                  createOrder={(data, actions) => {
                    return actions.order.create({
                      purchase_units: [{
                        amount: {
                          value: parseFloat(donationAmount).toFixed(2),
                          currency_code: "USD"
                        },
                        description: "Donation to Harris County Historical Society"
                      }]
                    });
                  }}
                  onApprove={async (data, actions) => {
                    await actions.order.capture();
                    alert('Thank you for your donation!');
                  }}
                />
              </div>
            </PayPalScriptProvider>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="support">
      <section className="page-hero">
        <h1>Support Our Mission</h1>
        <p>Join us in preserving Harris County's rich historical heritage</p>
      </section>

      <div className="page-container">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="support-tabs">
          <button 
            className={`tab-button ${activeTab === 'membership' ? 'active' : ''}`}
            onClick={() => setActiveTab('membership')}
          >
            Become a Member
          </button>
          <button 
            className={`tab-button ${activeTab === 'donate' ? 'active' : ''}`}
            onClick={() => setActiveTab('donate')}
          >
            Make a Donation
          </button>
        </div>

        {activeTab === 'membership' ? (
          <Formik
            initialValues={initialValues}
            validationSchema={membershipSchema}
            validate={validateSecondPerson}
            onSubmit={async (values, { setSubmitting }) => {
              // Payment is handled by the PayPal button below.
              setSubmitting(false);
            }}
          >
            {({ values, errors, touched, handleChange, setFieldValue, isSubmitting, isValid, dirty }) => (
              <Form className="membership-form">
                <div className="form-section">
                  <h3>Membership Type</h3>
                  <div className="membership-options">
                    {membershipOptions.map(option => (
                      <div
                        key={option.id}
                        className={`membership-option ${values.membershipType === option.id ? 'selected' : ''}`}
                        onClick={() => setFieldValue('membershipType', option.id)}
                      >
                        <h4>{option.label}</h4>
                        <p className="price">${option.price.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  {errors.membershipType && touched.membershipType && (
                    <div className="error">{errors.membershipType}</div>
                  )}
                </div>

                <div className="form-section">
                  <h3>Application Type</h3>
                  <div className="radio-group">
                    <label>
                      <Field type="radio" name="applicationType" value="new" />
                      New Membership
                    </label>
                    <label>
                      <Field type="radio" name="applicationType" value="renewal" />
                      Renewal
                    </label>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Primary Member Information</h3>
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="firstPerson.fullName">Full Name *</label>
                      <Field name="firstPerson.fullName" type="text" />
                      {errors.firstPerson?.fullName && touched.firstPerson?.fullName && (
                        <div className="error">{errors.firstPerson.fullName}</div>
                      )}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="firstPerson.email">Email Address *</label>
                      <Field name="firstPerson.email" type="email" />
                      {errors.firstPerson?.email && touched.firstPerson?.email && (
                        <div className="error">{errors.firstPerson.email}</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label htmlFor="firstPerson.phone">Phone Number</label>
                      <Field name="firstPerson.phone" type="tel" />
                    </div>
                  </div>
                </div>

                {values.membershipType === 'family' && (
                  <div className="form-section">
                    <h3>Secondary Member Information</h3>
                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="secondPerson.fullName">Full Name *</label>
                        <Field name="secondPerson.fullName" type="text" />
                        {errors.secondPerson?.fullName && touched.secondPerson?.fullName && (
                          <div className="error">{errors.secondPerson.fullName}</div>
                        )}
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="secondPerson.email">Email Address *</label>
                        <Field name="secondPerson.email" type="email" />
                        {errors.secondPerson?.email && touched.secondPerson?.email && (
                          <div className="error">{errors.secondPerson.email}</div>
                        )}
                      </div>
                      <div className="form-field">
                        <label htmlFor="secondPerson.phone">Phone Number</label>
                        <Field name="secondPerson.phone" type="tel" />
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-section">
                  <h3>Address Information</h3>
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="address.mailing">Mailing Address *</label>
                      <Field name="address.mailing" type="text" />
                      {errors.address?.mailing && touched.address?.mailing && (
                        <div className="error">{errors.address.mailing}</div>
                      )}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="address.city">City *</label>
                      <Field name="address.city" type="text" />
                      {errors.address?.city && touched.address?.city && (
                        <div className="error">{errors.address.city}</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label htmlFor="address.state">State *</label>
                      <Field name="address.state" type="text" />
                      {errors.address?.state && touched.address?.state && (
                        <div className="error">{errors.address.state}</div>
                      )}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="address.zipCode">Zip Code *</label>
                      <Field name="address.zipCode" type="text" />
                      {errors.address?.zipCode && touched.address?.zipCode && (
                        <div className="error">{errors.address.zipCode}</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label htmlFor="address.zipExt">Zip Ext (optional)</label>
                      <Field name="address.zipExt" type="text" />
                    </div>
                  </div>
                </div>

                <div className="paypal-container">
                    {Object.keys(errors).length > 0 ? (
                      <div className="validation-message">
                        <p>Please complete all required fields before proceeding to payment.</p>
                        <ul>
                          {Object.entries(errors).map(([field, error]) => {
                            if (typeof error === 'object') {
                              return Object.entries(error).map(([subField, subError]) => (
                                <li key={`${field}-${subField}`}>{subError}</li>
                              ));
                            }
                            return <li key={field}>{error}</li>;
                          })}
                        </ul>
                      </div>
                    ) : !dirty ? (
                      <div className="validation-message">
                        <p>Please fill out the form to proceed to payment.</p>
                      </div>
                    ) : (
                      <PayPalScriptProvider options={{
                        "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                        currency: "USD",
                        intent: "capture"
                      }}>
                        <PayPalButtons
                          style={{
                            layout: "vertical",
                            shape: "rect",
                            height: 55
                          }}
                          createOrder={(data, actions) => {
                            // Double check validation before creating order
                            if (Object.keys(errors).length > 0) {
                              return Promise.reject(new Error('Form validation failed'));
                            }
                            return actions.order.create({
                              purchase_units: [{
                                amount: {
                                  value: (membershipOptions.find(opt => opt.id === values.membershipType)?.price || 0).toFixed(2),
                                  currency_code: "USD"
                                },
                                description: `HCHS ${membershipOptions.find(opt => opt.id === values.membershipType)?.label} Membership`
                              }]
                            });
                          }}
                          onApprove={async (data, actions) => {
                            await handlePaymentSuccess(values, actions);
                          }}
                          onError={(err) => {
                            console.error('PayPal error:', err);
                            setError('There was an error processing your payment. Please try again.');
                          }}
                        />
                      </PayPalScriptProvider>
                    )}
                  {isProcessing && <div className="processing">Processing…</div>}
                  </div>
              </Form>
            )}
          </Formik>
        ) : (
          renderDonationSection()
        )}
      </div>
    </div>
  );
}

export default Support; 